import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
    NoteAccountOption,
    NoteAssignee,
    NoteDetailFactKey,
    NoteDetail,
    NoteDetailEditSubmit,
    NoteDetailReplySubmit,
    NoteEditableField,
    NoteTag,
} from './note-detail.types';
import { NoteDocumentAnchorSelect } from '../account-notes/account-notes.types';

//REV-21373 — Detailansicht eines Eintrags (Pure View). Kein Store, kein HTTP, keine Formatierung.
//Entwurf: ADR-0019 E7/E9.

/** Marken je Art — derselbe Satz wie im Konto-Bereich, damit beide Flaechen dasselbe anbieten. */
const TAGS_BY_KIND: Record<string, NoteTag[]> = {
    COMMENT: ['internal', 'closingMeeting'],
    TODO: ['todo', 'reviewer', 'client'],
};

@Component({
    selector: 'abcp-note-detail',
    templateUrl: './note-detail.component.html',
    styleUrls: ['./note-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpNoteDetailComponent implements OnChanges {
    /** `null` ⇒ es gibt ihn nicht (siehe `missing`) oder er laedt noch. */
    @Input() note: NoteDetail | null = null;

    @Input() assignees: NoteAssignee[] = [];

    /**
     * Die Konten des Abschlusses, für das Konto-Feld im Formular.
     *
     * ⚠️ Kommt HEREIN. Ein Pure View lädt keine Konten — dieselbe Regel wie bei `assignees`.
     */
    @Input() accounts: NoteAccountOption[] = [];

    @Input() loading = false;

    /**
     * ⚠️ „Gibt es nicht" und „liess sich nicht laden" sind VERSCHIEDENE Zustaende (AC9). Ein
     * geteilter Link auf einen geloeschten Eintrag ist kein Systemfehler, und ihn als solchen zu
     * melden schickt den Leser auf die Suche nach einer Stoerung, die es nicht gibt.
     */
    @Input() missing = false;
    @Input() error = false;

    /** Kontrolliert — der Host entscheidet, ob das Formular offen ist. */
    @Input() editing = false;

    /** Wer gerade angemeldet ist — entscheidet, welche Blase rechts steht. */
    @Input() currentUserId: string | null = null;

    /**
     * ⚠️ Kommt HEREIN, wie ueberall in diesem Buendel. Eine dumme Komponente, die „ueberfaellig"
     * selbst ausrechnet, rechnet es in der Zeitzone des Browsers statt in der des Mandats — und an
     * drei Stellen verschieden.
     */
    @Input() overdue = false;

    /** Shows the `Zurück` button. `false` when the page header carries the back button. */
    @Input() showBack = true;

    @Output() backRequested = new EventEmitter<void>();
    @Output() accountOpen = new EventEmitter<string>();
    @Output() anchorSelect = new EventEmitter<string>();
    /** REV-21369 (N4) — Zitat einer Markierung im Beleg geklickt. Wohin das führt, entscheidet der Host. */
    @Output() documentAnchorSelect = new EventEmitter<NoteDocumentAnchorSelect>();
    @Output() editStart = new EventEmitter<string>();
    @Output() editSubmit = new EventEmitter<NoteDetailEditSubmit>();
    @Output() editCancel = new EventEmitter<void>();
    @Output() deleteRequested = new EventEmitter<string>();
    @Output() replySubmit = new EventEmitter<NoteDetailReplySubmit>();
    /** Abhaken bzw. wieder öffnen — die Ansicht MELDET nur, umgeschaltet wird im Host. */
    @Output() doneToggle = new EventEmitter<string>();
    @Output() retryRequested = new EventEmitter<void>();

    // ── Entwuerfe: ansichts-lokal ─────────────────────────────────────────────────────────────
    editText = '';
    editDescription = '';
    editTags: NoteTag[] = [];
    /** UTC midnight of the due day — what the date picker reads and hands back. */
    editDue: Date | null = null;
    editAssigneeId: string | null = null;
    editAccountId: string | null = null;
    replyDraft = '';

    confirmDelete = false;

    /** Built once per list, not per check: the dropdown re-renders on every new `options` array. */
    accountOptions: { accountId: string; label: string }[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['accounts']) {
            this.accountOptions = this.accounts.map((a) => ({ accountId: a.accountId, label: `${a.number} · ${a.name}` }));
        }
        if (changes['editing'] || changes['note']) {
            this.seedEditDraft();
        }
    }

    private seedEditDraft(): void {
        if (!this.editing || !this.note) {
            return;
        }
        this.editText = this.note.text;
        this.editDescription = this.note.description ?? '';
        this.editTags = this.note.tags.slice();
        this.editAccountId = this.note.accountId ?? null;
        // ⚠️ Aus `dueValue` (`JJJJ-MM-TT`), NICHT aus der Zusammenfassungs-Zeile (`08.09.2026`):
        // die laesst sich nicht verlaesslich zurueck in ein Datum lesen. UTC-Mitternacht, wie sie
        // der Datumswaehler selbst liefert.
        this.editDue = this.note.dueValue ? new Date(`${this.note.dueValue}T00:00:00Z`) : null;
        this.editAssigneeId = this.note.assigneeId ?? null;
    }

    // ── Art und Rechte ────────────────────────────────────────────────────────────────────────

    get isTodo(): boolean {
        return !!this.note && this.note.kind === 'TODO';
    }

    /** Eine erledigte Pendenz traegt ihren Text durchgestrichen (AC2). */
    get isDone(): boolean {
        return !!this.note && !!this.note.done;
    }

    get availableTags(): NoteTag[] {
        return this.note ? TAGS_BY_KIND[this.note.kind] : [];
    }

    /** ⚠️ Das Feld erscheint nur, wenn der Eintrag es freigibt (AC6) — nicht ausgegraut. */
    mayEditField(field: NoteEditableField): boolean {
        return !!this.note && this.note.editableFields.indexOf(field) !== -1;
    }

    // ── Antworten ─────────────────────────────────────────────────────────────────────────────

    /**
     * ⚠️ Der Verlauf ist an der Pendenz SICHTBAR, aber ohne Antwortfeld (AC5). Eine Pendenz wird
     * abgearbeitet, nicht bequatscht — die Diskussion gehoert an den Kommentar.
     */
    get mayReply(): boolean {
        return !!this.note && !this.isTodo;
    }

    // ── Bearbeiten ────────────────────────────────────────────────────────────────────────────

    toggleEditTag(tag: NoteTag): void {
        const on = this.editTags.indexOf(tag) !== -1;
        if (!on) {
            this.editTags = [...this.editTags, tag];
        } else if (this.editTags.length > 1) {
            this.editTags = this.editTags.filter((t) => t !== tag);
        }
    }

    isEditTagOn(tag: NoteTag): boolean {
        return this.editTags.indexOf(tag) !== -1;
    }

    /**
     * Die EINE Zielgruppe einer Pendenz.
     *
     * ⚠️ Der Speicher kennt eine LISTE, die Pendenz benutzt davon genau einen Eintrag. Die
     * Liste hier nicht aufzugeben ist Absicht: Kommentare tragen wirklich mehrere
     * Klassifizierungen, und zwei Datenformen für dasselbe Feld wären teurer als dieser
     * Zugriff.
     */
    get soleTag(): NoteTag | null {
        return this.editTags.length > 0 ? this.editTags[0] : null;
    }

    setSoleTag(tag: NoteTag | null): void {
        this.editTags = tag === null ? [] : [tag];
    }

    /** Das Erstelldatum, wie es die Zusammenfassung schon kennt — hier nur zum Anzeigen. */
    get createdLabel(): string | null {
        const fact = this.note ? this.note.facts.filter((f) => f.key === 'created')[0] : undefined;
        return fact ? fact.value : null;
    }

    get canSubmitEdit(): boolean {
        return this.editText.trim().length > 0;
    }

    onEditSubmit(): void {
        if (!this.note || !this.canSubmitEdit) {
            return;
        }
        const payload: NoteDetailEditSubmit = {
            id: this.note.id,
            text: this.editText.trim(),
            tags: this.editTags,
        };
        if (this.mayEditField('description')) {
            // ⚠️ Leer geschrieben heißt GELÖSCHT, nicht „leerer Text“: sonst stünde in der
            // Datenbank ein `''`, das jede Leseseite zusätzlich zu `null` prüfen müsste.
            payload.description = this.editDescription.trim() === '' ? null : this.editDescription.trim();
        }
        if (this.mayEditField('due')) {
            // Leer geraeumt heisst KEINE Frist mehr, nicht „leere Zeichenkette“.
            payload.dueValue = this.editDue === null ? null : this.editDue.toISOString().slice(0, 10);
        }
        if (this.mayEditField('assignee')) {
            payload.assigneeId = this.editAssigneeId;
        }
        if (this.mayEditField('account')) {
            payload.accountId = this.editAccountId;
        }
        this.editSubmit.emit(payload);
    }

    // ── Loeschen ──────────────────────────────────────────────────────────────────────────────

    askDelete(): void {
        this.confirmDelete = true;
    }

    cancelDelete(): void {
        this.confirmDelete = false;
    }

    doDelete(): void {
        if (!this.note) {
            return;
        }
        this.confirmDelete = false;
        this.deleteRequested.emit(this.note.id);
    }

    // ── Sonstiges ─────────────────────────────────────────────────────────────────────────────

    onAccountOpen(): void {
        if (this.note && this.note.accountId) {
            this.accountOpen.emit(this.note.accountId);
        }
    }

    onAnchorClick(): void {
        const anchor = this.note ? this.note.anchor : null;
        if (!this.note || !anchor) {
            return;
        }
        if (anchor.blockId) {
            this.anchorSelect.emit(anchor.blockId);
        } else if (anchor.documentId) {
            this.documentAnchorSelect.emit({ noteId: this.note.id, documentId: anchor.documentId });
        }
    }

    get canSubmitReply(): boolean {
        return this.replyDraft.trim().length > 0;
    }

    onReplySubmit(): void {
        if (!this.note || !this.canSubmitReply) {
            return;
        }
        this.replySubmit.emit({ noteId: this.note.id, text: this.replyDraft.trim() });
        this.replyDraft = '';
    }

    trackById(_index: number, item: { id: string }): string {
        return item.id;
    }

    trackByKey(_index: number, item: { key: string }): string {
        return item.key;
    }

    /**
     * Die Zeilen, die als schlichter Text erscheinen — alles ausser den vier, die eine eigene
     * Darstellung haben (Status-Pille, Konto-Verweis, Avatar, Faelligkeit).
     */
    isPlainFact(key: NoteDetailFactKey): boolean {
        if (key === 'status' || key === 'author' || key === 'due') {
            return false;
        }
        // Das Konto ist nur dann ein Verweis, wenn es auch eines gibt.
        return !(key === 'account' && !!this.note && !!this.note.accountId);
    }

    /** Initialen fuer den Avatar. `null` ⇒ ein neutrales Zeichen statt eines leeren Kreises. */
    initialsOf(name: string | null | undefined): string {
        if (!name) {
            return '–';
        }
        const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
        if (parts.length === 0) {
            return '–';
        }
        const first = parts[0].charAt(0);
        const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
        return (first + last).toUpperCase();
    }

    isOwnReply(reply: { author: { name?: string | null } }): boolean {
        // Ohne angemeldete Person steht alles links — lieber neutral als falsch zugeordnet.
        return this.currentUserId !== null && !!reply.author.name && reply.author.name === this.currentUserId;
    }
}
